'use client';

import c from 'classnames';
import { PropsWithChildren, useContext, useEffect, useState } from 'react';

import { CartProvider } from '@contexts/CartContext';
import { OrganizationsProvider } from '@contexts/OrganizationsContext';
import { HeaderStateContext } from '@contexts/layout/HeaderStateContext';
import { ExposureEvent } from '@lib/flags/amplitude/ExposureEvent';
import { Flags } from '@lib/flags/flagConstants';
import { FooterLanguageMenu } from '@lib/types/cms/Footer';
import { Header as HeaderType } from '@lib/types/cms/Header';
import { User } from '@lib/types/common/User';
import { FastActionMenu } from '../FastActionMenu/FastActionMenu';
import { useScrollDirection } from './../../../lib/util/useScrollDirection';
import { HeaderLogo } from './Logo';
import { DesktopMenu } from './Navigation/Desktop/DesktopMenu';
import { MobileNavigationMenuModal } from './Navigation/Mobile/MobileNavigationMenuModal';
import { isSaleLink } from './Navigation/common/saleNav';
import { headerNavFormat } from './Navigation/headerNavFormat';
import { SearchWrapper } from './SearchWrapper';
import { SkipToMainButton } from './SkipToMainButton';
import { UserMenu } from './UserMenu/UserMenu';

type Props = {
  user?: User;
  headerData: HeaderType;
  languageMenu?: FooterLanguageMenu;
};

export const Header = ({
  user,
  headerData,
  languageMenu,
}: PropsWithChildren<Props>) => {
  const scrollDirection = useScrollDirection();
  const microcopyObject = headerData.microcopy ?? {};
  const searchPlaceholder =
    microcopyObject['layout.search-placeholder'] ?? 'Search for assets';
  const searchCompletionHint =
    microcopyObject['layout.search-completion-hint'] ??
    'Press Tab to accept the suggestion';
  const searchCompletionAccepted =
    microcopyObject['layout.search-completion-accepted'] ??
    'Search completed to “{query}”';
  const signedInProfileMenuItems = headerData.signedInUserMenu || [];
  const signedOutProfileMenuItems = headerData.signedOutUserMenu || [];
  const navigationItems = headerNavFormat(headerData.navigationItems || []);
  // CPS-7434: the Sale item is always visible in the desktop top nav, so we can
  // record exposure here once the desktop layout is active. On mobile the item
  // is hidden until the menu opens, so that exposure fires from the modal.
  const hasSaleLink = navigationItems.some((item) =>
    isSaleLink(item.mainLink.href)
  );
  const fastActionItems = headerData.fastActionsMenu || [];
  const logoImage = headerData.logo?.url;
  const currencyAriaLabel = microcopyObject['layout.currency'] || 'Currency';
  // need to start screenSize at null, because on the server render we don't know yet what the screen size is
  // if we render desktop or mobile version by default, and its actually the other it will result in a Next hydration error since the server and client render will be different
  const [screenSize, setScreenSize] = useState<null | 'desktop' | 'mobile'>(
    null
  );
  const { stickyHeaderEnabled } = useContext(HeaderStateContext);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setScreenSize('desktop');
      } else {
        setScreenSize('mobile');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <header
      className={c(
        'z-header relative top-0 bg-black text-white transition ease-linear lg:pb-0',
        {
          '-translate-y-full overflow-hidden': scrollDirection === 'down', //overflow hidden to prevent firefox scroll bug
          sticky: stickyHeaderEnabled,
        }
      )}
    >
      {hasSaleLink && screenSize === 'desktop' && (
        <ExposureEvent flagName={Flags.SALE_NAV_RED} />
      )}
      <SkipToMainButton
        label={microcopyObject['layout.skip-to-main-content']}
      />
      <div className="lg-container grid grid-cols-12 items-center py-4 lg:pb-0">
        <div className="col-span-6 mr-8 lg:col-span-3" data-test="header-logo">
          {logoImage && (
            <HeaderLogo
              imageUrl={logoImage}
              title={headerData.logo?.alt || ''}
              href={'/'}
            />
          )}
        </div>
        {/*
         * Search — one instance for both layouts, placed by CSS order rather
         * than by screenSize: after the logo on desktop, on its own row below
         * the header on mobile. Rendering it unconditionally puts the search
         * box in the server HTML, so it is on screen from first paint instead
         * of appearing at hydration — a blank header between every full
         * navigation otherwise. It also stops the box (and, on the SSR path,
         * its engine) remounting when the viewport crosses the breakpoint.
         */}
        <div className="order-last col-span-12 mt-8 lg:order-none lg:col-span-6 lg:mt-0 lg:w-auto lg:max-w-search-bar">
          <SearchWrapper
            searchPlaceholder={searchPlaceholder}
            searchCompletionHint={searchCompletionHint}
            searchCompletionAccepted={searchCompletionAccepted}
          />
        </div>
        <div className="col-span-6 ml-auto flex md:relative lg:col-span-3">
          {fastActionItems.length > 0 && (
            <CartProvider user={user}>
              <FastActionMenu
                showLoggedInOptions={Boolean(user)}
                items={fastActionItems}
              />
            </CartProvider>
          )}
          <OrganizationsProvider user={user}>
            <UserMenu
              user={user}
              microcopy={microcopyObject}
              sections={
                user ? signedInProfileMenuItems : signedOutProfileMenuItems
              }
            />
          </OrganizationsProvider>
          {/* Navigation Mobile */}
          <div className="lg:hidden">
            <MobileNavigationMenuModal
              navigationItems={navigationItems}
              currencyAriaLabel={currencyAriaLabel}
              microcopy={microcopyObject}
              languageMenu={languageMenu}
            />
          </div>
        </div>
        {/* Navigation Desktop */}
        <nav className="col-span-12 mt-4 hidden w-full lg:block">
          <DesktopMenu navigationItems={navigationItems} />
        </nav>
      </div>
    </header>
  );
};
